import type { ValidationSummaryItem } from '@workwave-tidal/form-ui/utils/ValidationSummary'
import type { FormField, FormError } from '@workwave-tidal/form-fairy'

import { useMemo } from 'react'

import { RESERVED_PASSWORD_TERMS } from '@/constants'

import { getStandardPasswordValidations } from './validations/getStandardPasswordValidations'
import { useTexts } from './useTexts'

type Options = {
  /** When provided, the password must not contain this username. */
  username: string
  /** Overrides the terms the password must not contain. Defaults to RESERVED_PASSWORD_TERMS. */
  reservedTerms: string[]
}

export function useStandardPasswordValidations<
  FORM_FIELDS extends Record<string, FormField>,
  FORM_ERRORS extends FormError<keyof FORM_FIELDS>,
>(field: keyof FORM_FIELDS, options?: Partial<Options>) {
  const texts = useTexts()

  const username = options?.username
  const reservedTerms = options?.reservedTerms || RESERVED_PASSWORD_TERMS

  return useMemo(() => {
    const validations = getStandardPasswordValidations<FORM_FIELDS, FORM_ERRORS>(
      field,
      username,
      reservedTerms,
    )

    const validationSummaryItems = [
      {
        ids: [`${field as string}_too_short`],
        field,
        message: texts.passwordLengthValidation,
      },
      {
        ids: [`${field as string}_excludes_repeated_patterns`],
        field,
        message: texts.passwordExcludesRepeatedPatternsValidation,
      },
      // the rules below are listed only when the data they compare against is available
      ...(username
        ? [
            {
              ids: [`${field as string}_excludes_username`],
              field,
              message: texts.passwordExcludesUsernameValidation,
            },
          ]
        : []),
      ...(reservedTerms.length > 0
        ? [
            {
              ids: [`${field as string}_excludes_reserved_terms`],
              field,
              message: texts.passwordExcludesReservedTermsValidation,
            },
          ]
        : []),
    ] as ValidationSummaryItem<FORM_FIELDS>[]

    return { validations, validationSummaryItems }
  }, [texts, field, username, reservedTerms])
}
